/**
 * 閲覧画面(タイピング・問題集)が読むデータの置き場(docs/20 §2.1)。
 *
 * 「現在の公開版(R2 の `content/releases/{id}/`)→ 取れなければ同梱 `/content/v1/`」。
 * 公開版の指し示しは `${VITE_CONTENT_BASE}content/current.json`(キャッシュしない)。
 * `VITE_CONTENT_BASE` は R2 バケットの公開ルート(Functions の CONTENT_PUBLIC_BASE と同じ場所)。
 * 管理画面はゲームと同じ公開URLを読むだけで、R2 へは書かない(書くのは管理用 Functions)。
 */

export const BUNDLED_RELEASE_ID = 'bundled-v1';

export interface AdminContentSource {
  /** 表示中の版。同梱予備版は `bundled-v1`。 */
  readonly releaseId: string;
  /** `stages/japanese.json` などを足す先(末尾 `/`)。 */
  readonly baseUrl: string;
}

export type SourceFetch = (
  url: string,
  init?: { readonly cache?: RequestCache },
) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

/** 公開版の ID は `r…` の形(Functions と同じ約束)。 */
const RELEASE_ID_PATTERN = /^r[A-Za-z0-9_-]{1,127}$/;

export function isValidReleaseId(value: unknown): value is string {
  return typeof value === 'string' && RELEASE_ID_PATTERN.test(value);
}

export function releaseBaseUrl(contentBase: string, releaseId: string): string {
  return `${contentBase}content/releases/${releaseId}/`;
}

export interface ResolveAdminContentSourceOptions {
  /** `VITE_CONTENT_BASE`(R2 バケットの公開ルート)。null なら同梱予備版。 */
  readonly contentBase: string | null;
  /** 同梱予備版の置き場(`/content/v1/`)。 */
  readonly bundledBase: string;
  readonly fetchFn?: SourceFetch;
}

/** 表示する版を決める。**例外を投げない**(失敗はすべて同梱予備版)。 */
export async function resolveAdminContentSource(
  options: ResolveAdminContentSourceOptions,
): Promise<AdminContentSource> {
  const bundled: AdminContentSource = {
    releaseId: BUNDLED_RELEASE_ID,
    baseUrl: options.bundledBase,
  };
  const contentBase = options.contentBase;
  if (contentBase === null) return bundled;
  const fetchFn: SourceFetch = options.fetchFn ?? ((url, init) => fetch(url, init));
  try {
    const response = await fetchFn(`${contentBase}content/current.json`, { cache: 'no-store' });
    if (!response.ok) return bundled;
    const pointer = (await response.json()) as { releaseId?: unknown } | null;
    const releaseId = pointer?.releaseId;
    if (!isValidReleaseId(releaseId)) return bundled;
    const baseUrl = releaseBaseUrl(contentBase, releaseId);
    const manifest = await fetchFn(`${baseUrl}manifest.json`);
    if (!manifest.ok) return bundled;
    return { releaseId, baseUrl };
  } catch {
    return bundled;
  }
}
